import { emptyCeremony, planningCeremony } from './ceremony'
import { emptyPhoto, planningPhoto } from './photo'
import { emptyReception, planningReception } from './reception'
import type {
  CatalogService,
  DayPart,
  Diet,
  GuestSettings,
  Member,
  Party,
  Person,
  ReplyAnswer,
  ScheduleEvent,
  Task,
  WeddingData,
} from './types'
import { planningVenues } from './venues'

const members: Member[] = [
  { id: 'm-anna', name: 'Anna Nováková', initials: 'AN', role: 'owner' },
  { id: 'm-petr', name: 'Petr Novák', initials: 'PN', role: 'editor' },
  { id: 'm-jana', name: 'Jana Svobodová', initials: 'JS', role: 'viewer' },
]

const catalog: CatalogService[] = [
  {
    id: 'c-loucen',
    name: 'Zámek Loučeň',
    category: 'venue',
    region: 'Středočeský kraj',
    description: 'Barokní zámek s parkem a labyrintem, obřad v zahradě i v kapli.',
    website: 'zamekloucen.cz',
    profile: {
      email: 'svatby@zamekloucen.cz',
      phone: '+420 325 591 111',
      address: 'Loučeň 1, 289 37 Loučeň',
      instagram: 'zamekloucen',
      facebook: 'zamekloucen',
    },
  },
  {
    id: 'c-stodola',
    name: 'Stodola Bělá',
    category: 'venue',
    region: 'Liberecký kraj',
    description: 'Rustikální stodola pro 120 hostů s ubytováním přímo na místě.',
    website: 'stodolabela.cz',
  },
  {
    id: 'c-foto-kral',
    name: 'Foto Král',
    category: 'photographer',
    region: 'Celá ČR',
    description: 'Reportážní svatební focení, celý den včetně přípravy.',
    website: 'fotokral.cz',
    profile: {
      email: 'jan@fotokral.cz',
      phone: '+420 777 123 456',
      instagram: 'fotokral',
      youtube: 'fotokral',
    },
  },
  {
    id: 'c-svetlo',
    name: 'Světlo a stín',
    category: 'photographer',
    region: 'Hlavní město Praha',
    description: 'Jemná dokumentární fotografie na film i digitál.',
    website: 'svetloastin.cz',
  },
  {
    id: 'c-film',
    name: 'Filmové vzpomínky',
    category: 'videographer',
    region: 'Celá ČR',
    description: 'Svatební klip a celovečerní záznam, dron v ceně.',
    website: 'filmovevzpominky.cz',
    profile: {
      email: 'ahoj@filmovevzpominky.cz',
      instagram: 'filmovevzpominky',
      youtube: 'filmovevzpominky',
      tiktok: 'filmovevzpominky',
    },
  },
  {
    id: 'c-gastro',
    name: 'Gastro Lipová',
    category: 'catering',
    region: 'Středočeský kraj',
    description: 'Catering pro 40–200 hostů, raut i servírované menu.',
    website: 'gastrolipova.cz',
    profile: {
      email: 'akce@gastrolipova.cz',
      phone: '+420 731 222 333',
      address: 'Lipová 12, Mladá Boleslav',
      facebook: 'gastrolipova',
    },
  },
  {
    id: 'c-kvetinarstvi',
    name: 'Květinářství Iris',
    category: 'florist',
    region: 'Hlavní město Praha',
    description: 'Svatební kytice, výzdoba obřadu a tabulí.',
    website: 'iris-kvetiny.cz',
    profile: {
      email: 'objednavky@iris-kvetiny.cz',
      phone: '+420 606 101 202',
      address: 'Korunní 40, Praha 2',
      instagram: 'iris.kvetiny',
    },
  },
  {
    id: 'c-dorty',
    name: 'Dorty od Lucie',
    category: 'cake-sweets',
    region: 'Středočeský kraj',
    description: 'Patrové dorty a sladký bar na míru.',
    website: 'dortyodlucie.cz',
  },
  {
    id: 'c-dj',
    name: 'DJ Martin',
    category: 'party-music',
    region: 'Celá ČR',
    description: 'Zábava do rána, ozvučení a světla v ceně.',
    website: 'djmartin.cz',
    profile: {
      email: 'martin@djmartin.cz',
      phone: '+420 608 444 555',
      instagram: 'djmartin.cz',
      tiktok: 'djmartincz',
    },
  },
  {
    id: 'c-kvartet',
    name: 'Smyčcové kvarteto Lyra',
    category: 'ceremony-music',
    region: 'Hlavní město Praha',
    description: 'Hudba k obřadu a k přípitku.',
    website: 'kvartetlyra.cz',
  },
  {
    id: 'c-vizaz',
    name: 'Studio Glow',
    category: 'makeup',
    region: 'Hlavní město Praha',
    description: 'Líčení nevěsty i hostů, zkouška v ateliéru.',
    website: 'studioglow.cz',
    profile: { email: 'studio@studioglow.cz', instagram: 'studio.glow', tiktok: 'studioglow' },
  },
  {
    id: 'c-oddavajici',
    name: 'Mgr. Eva Dvořáková',
    category: 'officiant',
    region: 'Středočeský kraj',
    description: 'Civilní i symbolické obřady, osobní setkání předem.',
    website: 'oddavajici-eva.cz',
    profile: { email: 'eva.dvorakova@email.cz', phone: '+420 724 666 777' },
  },
]

function person(
  id: string,
  firstName: string,
  lastName: string,
  attendance: Person['attendance'],
  extra: Partial<Person> = {},
): Person {
  return { id, firstName, lastName, attendance, child: false, diets: [], ...extra }
}

function party(
  id: string,
  circle: Party['circle'],
  persons: Person[],
  extra: Partial<Party> = {},
): Party {
  return {
    id,
    circle,
    persons,
    parts: allParts,
    replied: persons.some((p) => p.attendance === 'confirmed' || p.attendance === 'declined'),
    accommodation: false,
    transport: false,
    ...extra,
  }
}

const veg: Diet[] = ['vegetarian']

function answer(
  personId: string,
  firstName: string,
  lastName: string,
  attendance: ReplyAnswer['attendance'],
  diets: Diet[] = [],
): ReplyAnswer {
  return { personId, firstName, lastName, attendance, diets }
}

const dayParts: DayPart[] = [
  { id: 'ceremony', label: 'Obřad', time: '13:00' },
  { id: 'dinner', label: 'Svatební hostina', time: '14:30' },
  { id: 'party', label: 'Večerní zábava', time: '19:00' },
]
const allParts = dayParts.map((part) => part.id)

const guestSettings: GuestSettings = {
  ask: { diets: true, accommodation: true, transport: true, message: true },
  companions: 'listed',
  children: 'listed',
}

const parties: Party[] = [
  party('p-novakovi', 'bride', [
    person('g1', 'Jana', 'Nováková', 'confirmed', { diets: veg }),
    person('g2', 'Karel', 'Novák', 'confirmed'),
  ]),
  party(
    'p-dvorakovi',
    'groom',
    [
      person('g3', 'Martin', 'Dvořák', 'confirmed'),
      person('g4', 'Lucie', 'Dvořáková', 'confirmed', { diets: ['gluten-free'] }),
      person('g5', 'Ema', 'Dvořáková', 'confirmed', { child: true }),
      person('g6', 'Tomáš', 'Dvořák', 'confirmed', { child: true }),
    ],
    { repliedAt: '2026-10-02', message: 'Moc se těšíme! Budeme potřebovat dětskou židličku.' },
  ),
  party('p-svoboda', 'groom', [
    person('g7', 'Ondřej', 'Svoboda', 'confirmed'),
    person('g8', '', '', 'confirmed', { plusOne: true }),
  ]),
  party('p-horakova', 'bride', [person('g9', 'Klára', 'Horáková', 'waiting')], {
    replied: false,
  }),
  party(
    'p-cerni',
    'shared',
    [person('g10', 'Pavel', 'Černý', 'declined'), person('g11', 'Monika', 'Černá', 'declined')],
    { repliedAt: '2026-09-20', message: 'Bohužel jsme v zahraničí, držíme palce!' },
  ),
  party('p-babicka', 'bride', [person('g12', 'Marie', 'Nováková', 'confirmed', { diets: veg })], {
    accommodation: true,
    transport: true,
  }),
  party('p-prochazkovi', 'groom', [
    person('g13', 'Jiří', 'Procházka', 'invited'),
    person('g14', 'Alena', 'Procházková', 'invited'),
  ]),
  party('p-kucerova', 'bride', [
    person('g15', 'Tereza', 'Kučerová', 'confirmed', { diets: ['vegan'] }),
    person('g16', '', '', 'invited', { plusOne: true }),
  ]),
  party('p-vesely', 'groom', [person('g17', 'David', 'Veselý', 'confirmed')], {
    parts: ['party'],
    repliedAt: '2026-10-10',
  }),
  party('p-pokorni', 'shared', [
    person('g18', 'Filip', 'Pokorný', 'waiting'),
    person('g19', 'Barbora', 'Pokorná', 'waiting'),
    person('g20', 'Adam', 'Pokorný', 'waiting', { child: true }),
  ]),
  party('p-marek', 'groom', [
    person('g21', 'Lukáš', 'Marek', 'confirmed', { diets: ['lactose-free'] }),
    person('g22', 'Petra', 'Marková', 'confirmed'),
  ]),
  party(
    'p-kolegove',
    'bride',
    [
      person('g23', 'Simona', 'Benešová', 'invited'),
      person('g24', 'Hana', 'Králová', 'invited'),
      person('g25', 'Eva', 'Fialová', 'invited'),
    ],
    { name: 'Kolegyně z práce' },
  ),
  party('p-pospisil', 'groom', [person('g26', 'Roman', 'Pospíšil', 'declined')], {
    repliedAt: '2026-10-12',
  }),
  party(
    'p-urbanovi',
    'shared',
    [
      person('g27', 'Josef', 'Urban', 'confirmed'),
      person('g28', 'Věra', 'Urbanová', 'confirmed', { diets: veg }),
    ],
    { accommodation: true },
  ),
  party('p-zelenka', 'bride', [
    person('g29', 'Kristýna', 'Zelenková', 'waiting'),
    person('g30', '', '', 'invited', { plusOne: true }),
  ]),
]

const tasks: Task[] = [
  {
    id: 't-date',
    title: 'Doplnit termín svatby',
    note: '',
    dueDate: null,
    priority: 'normal',
    assigneeId: null,
    labels: [],
    done: true,
    automatic: 'wedding-date',
  },
  {
    id: 't-budget',
    title: 'Stanovit rámcový rozpočet',
    note: '',
    dueDate: null,
    priority: 'normal',
    assigneeId: null,
    labels: [],
    done: true,
    automatic: 'budget-total',
  },
  {
    id: 't-pay-photo',
    title: 'Zaplatit: Foto Král, záloha',
    note: '',
    dueDate: '2026-10-16',
    priority: 'high',
    assigneeId: 'm-petr',
    labels: ['Platby'],
    done: false,
    payment: { expenseId: 'e-photo', paymentId: 'pay-photo-1' },
  },
  {
    id: 't-contract',
    title: 'Podepsat smlouvu s místem',
    note: 'Zkontrolovat storno podmínky a termín úhrady zálohy.',
    dueDate: '2026-10-24',
    priority: 'high',
    assigneeId: 'm-anna',
    labels: ['Smlouvy'],
    done: false,
    context: { type: 'venue', label: 'Zámek Loučeň' },
  },
  {
    id: 't-tasting',
    title: 'Objednat ochutnávku menu',
    note: '',
    dueDate: '2026-10-31',
    priority: 'normal',
    assigneeId: null,
    labels: [],
    done: false,
    context: { type: 'reception' },
  },
  {
    id: 't-photo',
    title: 'Rozhodnout o focení a videu',
    note: '',
    dueDate: '2026-11-20',
    priority: 'normal',
    assigneeId: 'm-petr',
    labels: [],
    done: false,
    context: { type: 'photo' },
  },
  {
    id: 't-final-count',
    title: 'Nahlásit finální počet hostů cateringu',
    note: '',
    dueDate: '2027-05-28',
    priority: 'high',
    assigneeId: 'm-anna',
    labels: ['Hosté'],
    done: false,
    context: { type: 'reception' },
  },
  {
    id: 't-schedule-send',
    title: 'Poslat harmonogram dodavatelům',
    note: '',
    dueDate: '2027-06-05',
    priority: 'normal',
    assigneeId: 'm-petr',
    labels: [],
    done: false,
  },
  {
    id: 't-dress-pickup',
    title: 'Vyzvednout šaty a oblek',
    note: '',
    dueDate: '2027-06-09',
    priority: 'normal',
    assigneeId: null,
    labels: [],
    done: false,
  },
  {
    id: 't-rings-day',
    title: 'Předat prstýnky svědkovi',
    note: '',
    dueDate: '2027-06-12',
    priority: 'normal',
    assigneeId: 'm-petr',
    labels: [],
    done: false,
    context: { type: 'ceremony' },
  },
  {
    id: 't-thanks',
    title: 'Poslat poděkování hostům',
    note: '',
    dueDate: '2027-07-10',
    priority: 'low',
    assigneeId: null,
    labels: ['Hosté'],
    done: false,
  },
  {
    id: 't-music',
    title: 'Vybrat podobu hudby a zábavy',
    note: '',
    dueDate: null,
    priority: 'low',
    assigneeId: null,
    labels: [],
    done: false,
  },
  {
    id: 't-guests',
    title: 'Připravit první seznam hostů',
    note: '',
    dueDate: '2026-09-30',
    priority: 'normal',
    assigneeId: 'm-anna',
    labels: ['Hosté'],
    done: true,
  },
  {
    id: 't-ceremony',
    title: 'Rozhodnout o podobě obřadu',
    note: '',
    dueDate: '2026-11-05',
    priority: 'normal',
    assigneeId: null,
    labels: [],
    done: false,
    context: { type: 'ceremony' },
  },
  {
    id: 't-dress',
    title: 'Začít vybírat šaty, oblek a prsteny',
    note: '',
    dueDate: '2026-12-15',
    priority: 'normal',
    assigneeId: 'm-anna',
    labels: [],
    done: false,
  },
  {
    id: 't-beauty',
    title: 'Naplánovat líčení a účes',
    note: '',
    dueDate: '2027-03-01',
    priority: 'low',
    assigneeId: null,
    labels: [],
    done: false,
  },
  {
    id: 't-info',
    title: 'Připravit informace pro hosty',
    note: '',
    dueDate: '2027-02-01',
    priority: 'normal',
    assigneeId: null,
    labels: ['Hosté'],
    done: false,
  },
  {
    id: 't-schedule',
    title: 'Připravit první návrh harmonogramu',
    note: '',
    dueDate: null,
    priority: 'normal',
    assigneeId: null,
    labels: [],
    done: false,
  },
  {
    id: 't-venue-book',
    title: 'Rezervovat zámek',
    note: '',
    dueDate: '2026-10-02',
    priority: 'high',
    assigneeId: 'm-anna',
    labels: ['Smlouvy'],
    done: true,
  },
  {
    id: 't-decor',
    title: 'Ujasnit styl a zajištění výzdoby',
    note: '',
    dueDate: '2027-01-15',
    priority: 'low',
    assigneeId: null,
    labels: [],
    done: false,
  },
  {
    id: 't-pay-venue',
    title: 'Zaplatit: Zámek Loučeň, záloha',
    note: '',
    dueDate: '2026-10-28',
    priority: 'high',
    assigneeId: 'm-anna',
    labels: ['Platby'],
    done: false,
    payment: { expenseId: 'e-venue', paymentId: 'pay-venue-1' },
  },
  {
    id: 't-rings',
    title: 'Objednat snubní prsteny',
    note: '',
    dueDate: '2027-02-20',
    priority: 'normal',
    assigneeId: 'm-petr',
    labels: [],
    done: false,
  },
  {
    id: 't-invitations',
    title: 'Rozeslat odkaz na svatební web',
    note: 'Jeden odkaz pro všechny, hosté si na webu vyberou, kdo jsou.',
    dueDate: '2026-10-20',
    priority: 'high',
    assigneeId: 'm-anna',
    labels: ['Hosté'],
    done: false,
  },
  {
    id: 't-officiant',
    title: 'Domluvit schůzku s oddávající',
    note: '',
    dueDate: '2026-11-10',
    priority: 'normal',
    assigneeId: null,
    labels: [],
    done: false,
    context: { type: 'ceremony' },
  },
  {
    id: 't-cake',
    title: 'Vybrat svatební dort',
    note: '',
    dueDate: '2027-04-01',
    priority: 'low',
    assigneeId: null,
    labels: [],
    done: false,
    context: { type: 'reception' },
  },
]

function event(
  id: string,
  time: string | null,
  endTime: string | null,
  title: string,
  rest: Partial<Omit<ScheduleEvent, 'id' | 'time' | 'endTime' | 'title'>> = {},
): ScheduleEvent {
  return {
    id,
    time,
    endTime,
    title,
    description: '',
    location: '',
    audience: 'team',
    dayPartId: null,
    removed: false,
    ...rest,
  }
}

const schedule: ScheduleEvent[] = [
  event('s-makeup', '08:00', '10:00', 'Líčení a účes nevěsty', {
    location: 'Apartmá v zámku',
    owner: { type: 'supplier', id: 'sup-glow' },
    source: { section: 'beauty', label: 'Vizážista a stylistka' },
  }),
  event('s-florist', '09:00', null, 'Příjezd: Floristka · Květinářství Iris', {
    owner: { type: 'supplier', id: 'sup-iris' },
    source: { section: 'decor', label: 'Vizuál a dekorace' },
    removed: true,
  }),
  event('s-photo', '10:30', null, 'Příjezd: Fotograf · Foto Král', {
    owner: { type: 'supplier', id: 'sup-foto' },
    source: { section: 'photo', label: 'Foto a video' },
  }),
  event('s-prep-photo', '11:00', '12:00', 'Focení příprav', {
    location: 'Apartmá v zámku',
    owner: { type: 'supplier', id: 'sup-foto' },
    source: { section: 'photo', label: 'Foto a video' },
  }),
  event('s-catering', '12:30', null, 'Příjezd: Catering · Gastro Lipová', {
    description: 'Vjezd zadní branou, vykládka u kuchyně.',
    owner: { type: 'supplier', id: 'sup-gastro' },
    source: { section: 'reception', label: 'Jídlo a pití' },
  }),
  event('s-guests', '13:30', '14:00', 'Příjezd hostů', {
    description: 'Welcome drink na nádvoří.',
    location: 'Zámecké nádvoří',
    audience: 'guests',
    dayPartId: 'ceremony',
    owner: { type: 'member', id: 'm-anna' },
  }),
  event('s-ceremony', '14:00', '14:30', 'Obřad', {
    description: 'Při dešti v zámecké kapli.',
    location: 'Zámecká zahrada',
    audience: 'guests',
    dayPartId: 'ceremony',
    owner: { type: 'supplier', id: 'sup-eva' },
    source: { section: 'ceremony', label: 'Obřad' },
  }),
  event('s-toast', '14:30', '15:00', 'Přípitek a gratulace', {
    location: 'Zámecké nádvoří',
    audience: 'guests',
    dayPartId: 'ceremony',
    owner: { type: 'supplier', id: 'sup-gastro' },
    source: { section: 'reception', label: 'Jídlo a pití' },
  }),
  event('s-groups', '14:45', '15:30', 'Skupinové focení', {
    description: 'Rodina nevěsty, rodina ženicha, kamarádi, všichni.',
    location: 'Zámecký park',
    audience: 'guests',
    dayPartId: 'ceremony',
    owner: { type: 'supplier', id: 'sup-foto' },
    source: { section: 'photo', label: 'Foto a video' },
  }),
  event('s-lunch', '15:30', '17:00', 'Svatební oběd', {
    location: 'Velký sál',
    audience: 'guests',
    dayPartId: 'dinner',
    owner: { type: 'supplier', id: 'sup-gastro' },
    source: { section: 'reception', label: 'Jídlo a pití' },
  }),
  event('s-speech', null, null, 'Proslov svědků', {
    description: 'Čas doladíme s moderátorem.',
    location: 'Velký sál',
    audience: 'guests',
    dayPartId: 'dinner',
    owner: { type: 'member', id: 'm-petr' },
  }),
  event('s-cake', '18:00', '18:20', 'Krájení dortu', {
    location: 'Velký sál',
    audience: 'guests',
    dayPartId: 'dinner',
  }),
  event('s-sunset', '18:30', '19:00', 'Focení novomanželů při západu slunce', {
    location: 'Vyhlídka nad rybníkem',
    dayPartId: 'dinner',
    owner: { type: 'supplier', id: 'sup-foto' },
    source: { section: 'photo', label: 'Foto a video' },
  }),
  event('s-dj-arrival', '19:30', null, 'Příjezd: DJ · DJ Martin', {
    dayPartId: 'party',
    owner: { type: 'supplier', id: 'sup-dj' },
    source: { section: 'music', label: 'Hudba a zábava' },
  }),
  event('s-first-dance', '20:00', '20:10', 'První tanec', {
    location: 'Velký sál',
    audience: 'guests',
    dayPartId: 'party',
    owner: { type: 'supplier', id: 'sup-dj' },
  }),
  event('s-party', '20:15', '00:30', 'Volná zábava s DJ', {
    location: 'Velký sál',
    audience: 'guests',
    dayPartId: 'party',
    owner: { type: 'supplier', id: 'sup-dj' },
    source: { section: 'music', label: 'Hudba a zábava' },
  }),
  event('s-surprise', '22:00', '22:15', 'Překvapení pro nevěstu', {
    description: 'Ohňostroj na terase. Ví jen tým, nevěsta ne.',
    location: 'Terasa',
    dayPartId: 'party',
    owner: { type: 'member', id: 'm-petr' },
  }),
  event('s-bus', '00:30', null, 'Rozvoz hostů', {
    description: 'Autobus do Mladé Boleslavi a do Prahy.',
    location: 'Před zámkem',
    audience: 'guests',
    dayPartId: 'party',
    owner: { type: 'supplier', id: 'sup-bus' },
  }),
]

/** "Svatba v plánování": a wedding eight months before the day. */
export function planningWedding(): WeddingData {
  return {
    identity: { brideName: 'Anna', groomName: 'Petr', date: '2027-06-12' },
    venues: planningVenues(),
    ceremony: planningCeremony(),
    photo: planningPhoto(),
    reception: planningReception(),
    members,
    tasks: tasks.map((task) => (task.id === 't-ceremony' ? { ...task, done: true } : task)),
    dayParts,
    guestSettings,
    hiddenPrompts: [],
    parties,
    webReplies: [
      {
        id: 'r1',
        partyId: 'p-horakova',
        name: 'Klára Horáková',
        answers: [answer('g9', 'Klára', 'Horáková', 'confirmed')],
        accommodation: false,
        transport: false,
        message: 'Ráda přijdu, jen nevím, jestli s doprovodem.',
        receivedAt: '2026-10-18T19:12',
        status: 'new',
      },
      {
        id: 'r4',
        partyId: 'p-kucerova',
        name: 'Tereza Kučerová',
        answers: [
          answer('g15', 'Tereza', 'Kučerová', 'confirmed', ['vegan']),
          answer('g16', 'Tomáš', 'Beran', 'confirmed'),
        ],
        accommodation: true,
        transport: false,
        message: '',
        receivedAt: '2026-10-16T21:30',
        status: 'new',
      },
      {
        id: 'r5',
        partyId: 'p-prochazkovi',
        name: 'Jiří Procházka',
        answers: [
          answer('g13', 'Jiří', 'Procházka', 'confirmed'),
          answer('g14', 'Alena', 'Procházková', 'declined'),
        ],
        accommodation: false,
        transport: true,
        message: 'Alena bohužel ten víkend pracuje, já dorazím rád.',
        receivedAt: '2026-10-15T10:02',
        status: 'new',
      },
      {
        id: 'r6',
        partyId: 'p-pospisil',
        name: 'Roman Pospíšil',
        answers: [answer('g26', 'Roman', 'Pospíšil', 'declined')],
        accommodation: false,
        transport: false,
        message: 'Moc mě to mrzí, budu na služební cestě.',
        receivedAt: '2026-10-12T18:45',
        status: 'applied',
      },
      {
        id: 'r3',
        partyId: 'p-vesely',
        name: 'David Veselý',
        answers: [answer('g17', 'David', 'Veselý', 'confirmed')],
        accommodation: false,
        transport: false,
        message: '',
        receivedAt: '2026-10-10T21:05',
        status: 'applied',
      },
    ],
    suppliers: [
      {
        id: 'sup-loucen',
        name: 'Zámek Loučeň',
        category: 'venue',
        status: 'booked',
        contacts: [
          {
            id: 'sc1',
            name: 'Ing. Markéta Malá',
            role: 'Koordinátorka svateb',
            email: 'svatby@zamekloucen.cz',
            phone: '+420 602 111 222',
          },
        ],
        notes: 'Obřad v zahradě, při dešti kaple. Hostina v rytířském sále.',
        catalogServiceId: 'c-loucen',
      },
      {
        id: 'sup-stodola',
        name: 'Stodola Bělá',
        category: 'venue',
        status: 'inquiry',
        contacts: [],
        notes: 'Ubytování pro rodinu na dvě noci.',
        catalogServiceId: 'c-stodola',
      },
      {
        id: 'sup-penzion',
        name: 'Penzion U Lípy',
        category: 'venue',
        status: 'agreed',
        contacts: [],
        notes: 'Afterparty a snídaně pro hosty v neděli.',
        profile: { address: 'Lipová 4, Loučeň' },
      },
      {
        id: 'sup-foto',
        name: 'Foto Král',
        category: 'photographer',
        status: 'booked',
        contacts: [
          { id: 'sc2', name: 'Jan Král', email: 'jan@fotokral.cz', phone: '+420 777 123 456' },
        ],
        notes: '',
        catalogServiceId: 'c-foto-kral',
      },
      {
        id: 'sup-gastro',
        name: 'Gastro Lipová',
        category: 'catering',
        status: 'agreed',
        contacts: [
          {
            id: 'sc4',
            name: 'Lenka Lipová',
            email: 'akce@gastrolipova.cz',
            phone: '+420 731 222 333',
          },
        ],
        notes: 'Čekáme na upravenou nabídku s vegetariánským menu.',
        catalogServiceId: 'c-gastro',
      },
      {
        id: 'sup-dj',
        name: 'DJ Martin',
        category: 'party-music',
        status: 'agreed',
        contacts: [
          {
            id: 'sc5',
            name: 'Martin Beneš',
            email: 'martin@djmartin.cz',
            phone: '+420 608 444 555',
          },
        ],
        notes: '',
        catalogServiceId: 'c-dj',
      },
      {
        id: 'sup-iris',
        name: 'Květinářství Iris',
        category: 'florist',
        status: 'inquiry',
        statusChangedAt: '2026-09-28',
        contacts: [],
        notes: '',
        catalogServiceId: 'c-kvetinarstvi',
      },
      {
        id: 'sup-kapradi',
        name: 'Ateliér Kapradí',
        category: 'florist',
        status: 'inquiry',
        contacts: [
          {
            id: 'sc7',
            name: 'Tereza Horáková',
            email: 'tereza@kapradi.cz',
            phone: '+420 739 321 654',
          },
        ],
        notes: 'Líbí se nám lučním stylem, pošle ukázky kytic.',
        profile: {
          description: 'Malý ateliér, luční a sezonní květiny, kytice i výzdoba obřadu.',
          website: 'kapradi.cz',
          email: 'tereza@kapradi.cz',
          phone: '+420 739 321 654',
          instagram: 'atelier.kapradi',
        },
      },
      {
        id: 'sup-glow',
        name: 'Studio Glow',
        category: 'makeup',
        status: 'inquiry',
        contacts: [],
        notes: '',
        catalogServiceId: 'c-vizaz',
      },
      {
        id: 'sup-film',
        name: 'Filmové vzpomínky',
        category: 'videographer',
        status: 'inquiry',
        contacts: [],
        notes: '',
        catalogServiceId: 'c-film',
      },
      {
        id: 'sup-eva',
        name: 'Mgr. Eva Dvořáková',
        category: 'officiant',
        status: 'agreed',
        contacts: [
          {
            id: 'sc6',
            name: 'Eva Dvořáková',
            email: 'eva.dvorakova@email.cz',
            phone: '+420 724 666 777',
          },
        ],
        notes: '',
        catalogServiceId: 'c-oddavajici',
      },
      {
        id: 'sup-dorty',
        name: 'Cukrárna U Zlatého kosa',
        category: 'cake-sweets',
        status: 'cancelled',
        contacts: [],
        notes: 'Nemají termín.',
      },
      {
        id: 'sup-bus',
        name: 'Autobusy Hrubý',
        category: 'transport',
        status: 'inquiry',
        contacts: [{ id: 'sc3', name: 'Pavel Hrubý', email: '', phone: '+420 603 999 000' }],
        notes: 'Svoz hostů z Prahy a zpět o půlnoci.',
        profile: { phone: '+420 603 999 000', address: 'Nádražní 5, Benátky nad Jizerou' },
      },
    ],
    catalog,
    budget: {
      total: 450_000,
      expenses: [
        {
          id: 'e-venue',
          estimate: false,
          title: 'Zámek Loučeň',
          category: 'venue',
          amount: 160_000,
          supplierId: 'sup-loucen',
          payments: [
            {
              id: 'pay-venue-1',
              name: 'Záloha',
              amount: 50_000,
              dueDate: '2026-10-28',
              paid: false,
              method: 'transfer',
            },
            {
              id: 'pay-venue-2',
              name: 'Doplatek',
              amount: 110_000,
              dueDate: '2027-05-30',
              paid: false,
              method: 'transfer',
            },
          ],
        },
        {
          id: 'e-catering',
          estimate: false,
          title: 'Gastro Lipová',
          category: 'catering',
          amount: 96_000,
          supplierId: 'sup-gastro',
          payments: [],
        },
        {
          id: 'e-photo',
          estimate: false,
          title: 'Foto Král',
          category: 'photographer',
          amount: 38_000,
          supplierId: 'sup-foto',
          payments: [
            {
              id: 'pay-photo-1',
              name: 'Záloha',
              amount: 8_000,
              dueDate: '2026-10-16',
              paid: false,
              method: 'transfer',
            },
            {
              id: 'pay-photo-2',
              name: 'Doplatek',
              amount: 30_000,
              dueDate: '2027-06-12',
              paid: false,
              method: 'cash',
            },
          ],
        },
        {
          id: 'e-dj',
          estimate: false,
          title: 'DJ Martin',
          category: 'party-music',
          amount: 0,
          supplierId: 'sup-dj',
          payments: [],
        },
        {
          id: 'e-dress',
          estimate: false,
          title: 'Svatební šaty',
          category: 'bride-attire',
          amount: 42_000,
          payments: [
            {
              id: 'pay-dress-1',
              name: 'Záloha',
              amount: 12_000,
              dueDate: '2026-09-15',
              paid: true,
              method: 'card',
            },
            {
              id: 'pay-dress-2',
              name: 'Doplatek',
              amount: 30_000,
              dueDate: '2027-05-15',
              paid: false,
              method: 'card',
            },
          ],
        },
        {
          id: 'e-rings',
          estimate: true,
          title: 'Snubní prsteny',
          category: 'rings',
          amount: 24_000,
          payments: [],
        },
        {
          id: 'e-officiant',
          estimate: false,
          title: 'Mgr. Eva Dvořáková',
          category: 'officiant',
          amount: 6_000,
          supplierId: 'sup-eva',
          payments: [
            {
              id: 'pay-off-1',
              name: 'Celá částka',
              amount: 6_000,
              dueDate: '2027-06-01',
              paid: false,
              method: 'cash',
            },
          ],
        },
        {
          id: 'e-flowers',
          estimate: true,
          title: 'Květiny a výzdoba',
          category: 'florist',
          amount: 35_000,
          payments: [],
        },
        {
          id: 'e-beauty',
          estimate: true,
          title: 'Líčení a účes',
          category: 'makeup',
          amount: 8_000,
          payments: [],
        },
        {
          id: 'e-invites',
          estimate: false,
          title: 'Svatební oznámení',
          category: 'stationery',
          amount: 5_500,
          payments: [
            {
              id: 'pay-invites-1',
              name: 'Celá částka',
              amount: 5_500,
              dueDate: '2026-08-20',
              paid: true,
              method: 'card',
            },
          ],
        },
      ],
    },
    schedule,
    // Sent to the photographer before the toast and group photos were planned.
    scheduleShares: [{ supplierId: 'sup-foto', sentAt: '2026-10-10', signature: 'earlier' }],
    website: {
      published: true,
      rsvpEnabled: true,
      link: 'slibby.cz/svatba/anna-a-petr',
      rsvpDeadline: '2027-04-30',
    },
  }
}

/** "Nová svatba": right after registration, with the 13 starter tasks. */
export function newWedding(): WeddingData {
  const starterIds = new Set([
    't-date',
    't-budget',
    't-guests',
    't-ceremony',
    't-photo',
    't-music',
    't-beauty',
    't-decor',
    't-dress',
    't-info',
    't-schedule',
  ])
  const starters: Task[] = tasks
    .filter((task) => starterIds.has(task.id))
    .map((task) => ({
      ...task,
      done: false,
      dueDate: null,
      assigneeId: null,
      labels: [],
      context: undefined,
    }))
  return {
    identity: { brideName: '', groomName: '', date: null },
    venues: [],
    ceremony: emptyCeremony(),
    photo: emptyPhoto(),
    reception: emptyReception(),
    members: members.slice(0, 1),
    tasks: [
      ...starters,
      {
        id: 't-venue-new',
        title: 'Ujasnit místo konání svatby',
        note: '',
        dueDate: null,
        priority: 'normal',
        assigneeId: null,
        labels: [],
        done: false,
      },
      {
        id: 't-food-new',
        title: 'Rozhodnout, kdo zajistí jídlo a pití',
        note: '',
        dueDate: null,
        priority: 'normal',
        assigneeId: null,
        labels: [],
        done: false,
      },
    ],
    dayParts: dayParts.map(({ time: _time, ...part }) => part),
    guestSettings,
    hiddenPrompts: [],
    parties: [],
    webReplies: [],
    suppliers: [],
    catalog,
    budget: { total: null, expenses: [] },
    schedule: [],
    scheduleShares: [],
    website: {
      published: false,
      rsvpEnabled: false,
      link: 'slibby.cz/svatba/moje-svatba',
      rsvpDeadline: null,
    },
  }
}
